/**
 * SGI HRMS — API base URL resolver
 *
 * Strategy:
 * 1. If the user is on a custom production domain (hrms.sginstitute.in), use SAME ORIGIN.
 *    The backend is reverse-proxied at /api/* on that same host, so this is always correct
 *    and avoids CORS entirely.
 * 2. If on the Emergent preview/host domain, fall back to REACT_APP_BACKEND_URL (baked at build time).
 * 3. As a safety net, always fall back to window.location.origin so we never end up with `undefined`.
 *
 * This fixes the bug where the React frontend was built with REACT_APP_BACKEND_URL pointing
 * to `talent-pipeline-65.emergent.host` but the user accesses the app on `hrms.sginstitute.in`,
 * causing cross-origin requests that the browser blocks.
 */
export function getApiBase() {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  const origin = window.location.origin;
  const host = window.location.hostname;
  const buildTime = process.env.REACT_APP_BACKEND_URL;

  // Treat these as "Emergent's own hosting" — for those, the build-time URL is correct.
  const isEmergentHost = /\.(emergentagent\.com|emergent\.host)$/i.test(host);
  if (isEmergentHost && buildTime) return buildTime;

  // Localhost development — honour the env var so devs can point at a remote backend.
  if (host === 'localhost' || host === '127.0.0.1') {
    return buildTime || origin;
  }

  // Custom domain — always use same-origin so /api/* hits the backend behind the same proxy.
  return origin;
}

// Eager singleton for modules that just want a string at import time.
export const API_BASE = getApiBase();
